import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'

export const useModuleProgress = (moduleId: string) => {
  const [allContentProgress, setAllContentProgress] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const fetchModuleProgress = async () => {
    try {
      setLoading(true)
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user?.id) {
        setLoading(false)
        return
      }

      console.log('useModuleProgress - Fetching progress for module:', moduleId)

      // Get all content for this module
      const { data: contentData, error: contentError } = await supabase
        .from('learn_content')
        .select('id')
        .eq('module_id', moduleId)

      if (contentError) throw contentError

      if (contentData && contentData.length > 0) {
        const contentIds = contentData.map(content => content.id)
        console.log('useModuleProgress - Found content IDs:', contentIds)
        
        // Get progress for all content in this module
        const { data: progressData, error: progressError } = await supabase
          .from('user_content_progress')
          .select('*')
          .eq('user_id', session.user.id)
          .in('content_id', contentIds)

        if (progressError) throw progressError
        console.log('useModuleProgress - Progress data:', progressData)
        setAllContentProgress(progressData || [])
      } else {
        console.log('useModuleProgress - No content found for module')
        setAllContentProgress([])
      }
    } catch (error) {
      console.error('Error fetching module progress:', error)
    } finally {
      setLoading(false)
    }
  }

  const isContentCompleted = useCallback((contentId: string) => {
    const completed = allContentProgress.some(p => p.content_id === contentId && p.completed)
    console.log('useModuleProgress - isContentCompleted:', contentId, completed, 'allProgress:', allContentProgress)
    return completed
  }, [allContentProgress])

  const markContentAsCompleted = async (contentId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user?.id) return

      const { data, error } = await supabase
        .from('user_content_progress')
        .upsert({
          user_id: session.user.id,
          content_id: contentId,
          completed: true,
          completed_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,content_id'
        })
        .select()
        .single()

      if (error) throw error

      // Refresh progress data
      await fetchModuleProgress()

      return data
    } catch (error) {
      console.error('Error marking content as completed:', error)
      throw error
    }
  }

  const getModuleCompletionStats = useCallback((moduleContent: any[]) => {
    const completed = moduleContent.filter(content => 
      isContentCompleted(content.id)
    ).length
    
    const total = moduleContent.length
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0
    const isFullyCompleted = completed === total && total > 0

    return {
      completed,
      total,
      percentage,
      isFullyCompleted
    }
  }, [isContentCompleted])

  // Set up real-time subscription for progress changes in this module
  useEffect(() => {
    const setupRealtimeSubscription = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.user?.id) {
          console.log('No session available for module progress subscription')
          return
        }

        // Get all content IDs for this module
        const { data: contentData, error: contentError } = await supabase
          .from('learn_content')
          .select('id')
          .eq('module_id', moduleId)

        if (contentError || !contentData) {
          console.log('No content found for module:', moduleId)
          return
        }

        const contentIds = contentData.map(content => content.id)
        
        // Subscribe to changes for all content in this module
        const channel = supabase
          .channel(`module-progress-${moduleId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'user_content_progress',
              filter: `user_id=eq.${session.user.id} AND content_id=in.(${contentIds.join(',')})`
            },
            () => {
              console.log('Module progress changed - refreshing data')
              fetchModuleProgress()
            }
          )
          .subscribe()

        return () => {
          supabase.removeChannel(channel)
        }
      } catch (error) {
        console.error('Error setting up module progress subscription:', error)
        return () => {}
      }
    }

    const cleanup = setupRealtimeSubscription()
    return () => {
      cleanup.then(cleanupFn => cleanupFn?.())
    }
  }, [moduleId])

  useEffect(() => {
    if (moduleId) {
      fetchModuleProgress()
    }
  }, [moduleId])

  return {
    loading,
    allContentProgress,
    isContentCompleted,
    markContentAsCompleted,
    getModuleCompletionStats,
    refetch: fetchModuleProgress
  }
}
